import axiosInstance, { getNewCancelToken } from '@/services/_AxiosInstance';
import { browserUtcOffsetHours } from '@/util/apiDatetime';

export default {
  /**
   * GET `/summary/aggregated/period/{start}/{end}` — deret waktu + rollup per tipe device.
   *
   * Balikin response axios UTUH: baca `res.data.data[devcType]`. Tiap tipe punya
   * `{ summary, data }`. `summary` itu skalar buat seluruh window, `data` peta
   * timestamp detik -> baris. Backend gak ngunci timestampnya ke grid jam, jadi
   * pemanggil membucketkan sendiri ke 3600 detik (src/store/asset.js:63).
   *
   * Satuan yang gak pernah ditulis backend:
   * - `gps.summary.averageSpeed` km/h, dikonversi di src/util/vesselDetail.js:278
   * - `gps.summary.totalDistance` meter
   * - `gps.summary.movingTime` dan `totalDuration` detik
   * - `flowmeter.summary.meEngineCons` liter, src/util/vesselDetail.js:288
   *
   * `end` EKSKLUSIF — backend pakai `<`, bukan `<=` (src/util/fms-summary.js:196).
   *
   * Bucket paling akhir hampir selalu kosong buat rentang yang ujungnya "sekarang",
   * karena jam berjalan belum diagregasi backend (src/store/asset.js:624). Ambil
   * bucket terisi terakhir, bukan bucket terakhir.
   *
   * Window yang beneran kosong balikin semua nol, bukan field yang ilang — cek
   * `gps.summary.totalDuration > 0` dulu sebelum nampilin (src/util/vesselDetail.js:274).
   *
   * @param {Object} options
   * @param {string[]} options.devcType tipe device yang diminta, mis. ['gps','rpm','flowmeter','ae']
   * @param {number|string} options.devcMassId id asset
   * @param {number} options.start unix detik, inklusif
   * @param {number} options.end unix detik, EKSKLUSIF
   * @param {string} [options.aggregatedUnit] 'MINUTE' | 'HOUR' | 'DAY', huruf besar
   * @param {number} [options.aggregatedLength] panjang satu bucket dalam unit di atas
   * @param {boolean} [options.showDetails] true = blok `data` ikut dikirim
   * @param {number} [options.timezone] offset jam browser, default browserUtcOffsetHours()
   * @returns {Promise<import('axios').AxiosResponse<{data: Object.<string, {summary: Object, data: Object}>, status: import('@/services/_AxiosInstance').ApiStatus}>>}
   *   response axios utuh — jalur bacanya `res.data.data[devcType].summary`
   */
  getSummary({
    devcType,
    devcMassId,
    start,
    end,
    aggregatedUnit,
    aggregatedLength,
    showDetails,
    timezone = browserUtcOffsetHours(),
  }) {
    return axiosInstance.get(`/summary/aggregated/period/${start}/${end}`, {
      params: {
        devcType,
        devcMassId,
        start,
        end,
        aggregatedUnit,
        aggregatedLength,
        showDetails,
        timezone,
      },
      cancelToken: getNewCancelToken(),
    });
  },

  /**
   * GET `/summary/trail/{start}/{end}` — trail posisi buat peta.
   *
   * Payload kompak dan bentuknya beda sendiri: `res.data.data` =
   * `{ fields: string[], trail: any[][] }`, array-of-array yang diindeks lewat
   * `fields`, bukan objek per titik. Parser resminya `parseTrailResponse` di
   * src/util/trailData.js:9.
   *
   * `fields` yang dibaca FE: timestamp, latitude, longitude, speed, track,
   * waveHeight, wavePeriod, seaState. Tiga terakhir cuma ada di sebagian asset —
   * parser ngecek `fields.indexOf(...) < 0` lalu ngisi undefined, bukan null.
   *
   * Butuh permission read Summary_Trail. Ditolak izin balik `status.errorCode` 412
   * lewat HTTP 200, jadi axios gak throw.
   *
   * aggregatedUnit HOUR x 1 dipatok di sini karena itu interval yang dilayani cache
   * backend; jangan dijadiin parameter tanpa ngecek ke backend dulu.
   *
   * @param {Object} options
   * @param {number|string} options.devcMassId id asset
   * @param {number} options.start unix detik, inklusif
   * @param {number} options.end unix detik, eksklusif
   * @param {number} [options.timezone] offset jam browser, default browserUtcOffsetHours()
   * @returns {Promise<import('axios').AxiosResponse<{data: {fields: string[], trail: Array<Array<number|string|null>>}, status: import('@/services/_AxiosInstance').ApiStatus}>>}
   *   response axios utuh — jalur bacanya `res.data.data.trail`
   */
  getTrail({
    devcMassId,
    start,
    end,
    timezone = browserUtcOffsetHours(),
  }) {
    return axiosInstance.get(`/summary/trail/${start}/${end}`, {
      params: {
        devcMassId,
        aggregatedUnit: 'HOUR',
        aggregatedLength: 1,
        timezone,
      },
      cancelToken: getNewCancelToken(),
    });
  },

  /**
   * GET `/summary/asset/period/{start}/{end}` — rollup skalar doang (BE 2026-08-16).
   *
   * Gak ngirim blok `data` (deret waktu), jadi payloadnya jauh lebih kecil dari
   * getSummary. Baca `res.data.data[devcType].summary`.
   *
   * Field yang dibaca FE hari ini (src/util/trailData.js:80 rollupPointDetail):
   * - `gps.summary.totalDistance` meter
   * - `gps.summary.averageSpeed` km/h — SAMA kayak endpoint lama, tetep harus
   *   dikonversi. `speedKnots` yang dibaca peta itu bikinan FE di trailData.js:93,
   *   bukan field dari backend.
   * - `gps.summary.lastPosition.zone.name` nama zona buat label lokasi
   * - `rpm.summary.average.{PORT,CENTER,STARBOARD}`
   * - `ae.summary.runningSeconds` detik, key-nya `AE1`, `AE2`, dst
   * - `flowmeter.summary.{PORT_IN,STARBOARD_IN}._averageFlowrate`
   *
   * `res.data.meta.unavailable` = daftar devcType yang BELUM dilayani rollup. Tipe di
   * situ artinya "gak ada datanya", beda dari nol — jangan ditampilin sebagai 0
   * (src/store/asset.js:681). powermeter termasuk yang belum dilayani.
   *
   * RAGU: channel PORT/STARBOARD di rollup kemungkinan ketuker dibanding getSummary
   * (catatan src/store/asset.js:794). Jangan dipakai per-channel sebelum dicek ke
   * payload nyata.
   *
   * @param {Object} options
   * @param {string[]} options.devcType tipe device yang diminta
   * @param {number|string} options.devcMassId id asset
   * @param {number} options.start unix detik, inklusif
   * @param {number} options.end unix detik, eksklusif
   * @returns {Promise<import('axios').AxiosResponse<{data: Object.<string, {summary: Object}>, meta: {unavailable: string[]}, status: import('@/services/_AxiosInstance').ApiStatus}>>}
   *   response axios utuh — jalur bacanya `res.data.data[devcType].summary`
   */
  getAssetSummaryRollup({
    devcType,
    devcMassId,
    start,
    end,
  }) {
    return axiosInstance.get(`/summary/asset/period/${start}/${end}`, {
      params: {
        devcType,
        devcMassId,
      },
      cancelToken: getNewCancelToken(),
    });
  },
};
